import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Form, Modal, Badge, Nav, Table } from 'react-bootstrap';
import { Plus, PersonFill, FileText, Capsule, Calendar, ExclamationTriangle } from 'react-bootstrap-icons';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const MedicalRecords = ({ activeProfileId }) => {
    const [activeTab, setActiveTab] = useState('records');
    const [doctors, setDoctors] = useState([]);
    const [medicalRecords, setMedicalRecords] = useState([]);
    const [medications, setMedications] = useState([]);
    const [sickLeaves, setSickLeaves] = useState([]);
    const [healthEvents, setHealthEvents] = useState([]);
    
    // Modal states
    const [showDoctorModal, setShowDoctorModal] = useState(false);
    const [showRecordModal, setShowRecordModal] = useState(false);
    const [showMedicationModal, setShowMedicationModal] = useState(false);
    const [showSickLeaveModal, setShowSickLeaveModal] = useState(false);
    const [showHealthEventModal, setShowHealthEventModal] = useState(false);
    
    // Form states
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedMedication, setSelectedMedication] = useState(null);
    const [selectedSickLeave, setSelectedSickLeave] = useState(null);
    const [selectedHealthEvent, setSelectedHealthEvent] = useState(null);

    useEffect(() => {
        if (activeProfileId) {
            fetchDoctors();
            fetchMedicalRecords();
            fetchMedications();
            fetchSickLeaves();
            fetchHealthEvents();
        }
    }, [activeProfileId]);

    const fetchDoctors = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/doctors`);
            setDoctors(response.data);
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    const fetchMedicalRecords = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/medical-records`);
            setMedicalRecords(response.data);
        } catch (error) {
            console.error('Error fetching medical records:', error);
        }
    };

    const fetchMedications = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/medications`);
            setMedications(response.data);
        } catch (error) {
            console.error('Error fetching medications:', error);
        }
    };

    const fetchSickLeaves = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/sick-leave`);
            setSickLeaves(response.data);
        } catch (error) {
            console.error('Error fetching sick leaves:', error);
        }
    };

    const fetchHealthEvents = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/health-events`);
            setHealthEvents(response.data);
        } catch (error) {
            console.error('Error fetching health events:', error);
        }
    };

    const getDoctorName = (doctorId) => {
        const doctor = doctors.find(d => d.id === doctorId);
        return doctor ? doctor.name : 'Unknown Doctor';
    };

    const handleEditDoctor = (doctor) => {
        setSelectedDoctor(doctor);
        setShowDoctorModal(true);
    };

    const handleSubmitDoctor = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            if (selectedDoctor) {
                await axios.put(`${API_BASE_URL}/api/life/doctors/${selectedDoctor.id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/doctors`, formData);
            }
            fetchDoctors();
            setShowDoctorModal(false);
            setSelectedDoctor(null);
            e.target.reset();
        } catch (error) {
            console.error('Error saving doctor:', error);
        }
    };

    const handleEditHealthEvent = (event) => {
        setSelectedHealthEvent(event);
        setShowHealthEventModal(true);
    };

    const handleSubmitHealthEvent = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            if (selectedHealthEvent) {
                await axios.put(`${API_BASE_URL}/api/life/health-events/${selectedHealthEvent.id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/health-events`, formData);
            }
            fetchHealthEvents();
            setShowHealthEventModal(false);
            setSelectedHealthEvent(null);
            e.target.reset();
        } catch (error) {
            console.error('Error saving health event:', error);
        }
    };

    const handleEditMedication = (medication) => {
        setSelectedMedication(medication);
        setShowMedicationModal(true);
    };

    const handleSubmitMedication = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            if (selectedMedication) {
                await axios.put(`${API_BASE_URL}/api/life/medications/${selectedMedication.id}`, formData);
            } else {
                await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/medications`, formData);
            }
            fetchMedications();
            setShowMedicationModal(false);
            setSelectedMedication(null);
            e.target.reset();
        } catch (error) {
            console.error('Error saving medication:', error);
        }
    };

    return (
        <div className="medical-records-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>Medical Records</h4>
            </div>

            <Nav variant="tabs" className="mb-4">
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'records'} 
                        onClick={() => setActiveTab('records')}
                    >
                        <FileText className="me-2" />
                        Medical Records
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'doctors'} 
                        onClick={() => setActiveTab('doctors')}
                    >
                        <PersonFill className="me-2" />
                        Doctors
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'medications'} 
                        onClick={() => setActiveTab('medications')}
                    >
                        <Capsule className="me-2" />
                        Medications
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'sick-leave'} 
                        onClick={() => setActiveTab('sick-leave')}
                    >
                        <Calendar className="me-2" />
                        Sick Leave
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link 
                        active={activeTab === 'events'} 
                        onClick={() => setActiveTab('events')}
                    >
                        <ExclamationTriangle className="me-2" />
                        Health Events
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {activeTab === 'records' && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Medical Records</h5>
                        <Button variant="primary" onClick={() => setShowRecordModal(true)}>
                            <Plus /> Add Record
                        </Button>
                    </div>
                    
                    {medicalRecords.length === 0 ? (
                        <Card className="text-center p-4">
                            <p className="text-muted">No medical records yet. Add your first medical record to get started!</p>
                        </Card>
                    ) : (
                        <div className="records-list">
                            {medicalRecords.map(record => (
                                <Card key={record.id} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6>{record.visit_type}</h6>
                                                <p className="text-muted mb-1">
                                                    {new Date(record.visit_date).toLocaleDateString()} 
                                                    {record.doctor_id && ` • ${getDoctorName(record.doctor_id)}`}
                                                </p>
                                                {record.symptoms && <p><strong>Symptoms:</strong> {record.symptoms}</p>}
                                                {record.diagnosis && <p><strong>Diagnosis:</strong> {record.diagnosis}</p>}
                                                {record.treatment && <p><strong>Treatment:</strong> {record.treatment}</p>}
                                                {record.prescription && <p><strong>Prescription:</strong> {record.prescription}</p>}
                                                {record.notes && <p><strong>Notes:</strong> {record.notes}</p>}
                                                {record.cost && <Badge bg="info">${record.cost}</Badge>}
                                            </div>
                                            <Button variant="outline-primary" size="sm">
                                                Edit
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'doctors' && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Doctors</h5>
                        <Button variant="primary" onClick={() => setShowDoctorModal(true)}>
                            <Plus /> Add Doctor
                        </Button>
                    </div>
                    
                    {doctors.length === 0 ? (
                        <Card className="text-center p-4">
                            <p className="text-muted">No doctors added yet. Add your doctors to track your medical visits!</p>
                        </Card>
                    ) : (
                        <Row>
                            {doctors.map(doctor => (
                                <Col md={6} lg={4} key={doctor.id} className="mb-3">
                                    <Card>
                                        <Card.Body>
                                            <h6>{doctor.name}</h6>
                                            {doctor.specialty && <p className="text-muted">{doctor.specialty}</p>}
                                            {doctor.clinic_hospital && <p><strong>Clinic:</strong> {doctor.clinic_hospital}</p>}
                                            {doctor.phone && <p><strong>Phone:</strong> {doctor.phone}</p>}
                                            {doctor.email && <p><strong>Email:</strong> {doctor.email}</p>}
                                            <Button variant="outline-primary" size="sm" onClick={() => handleEditDoctor(doctor)}>
                                                Edit
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}
                </div>
            )}

            {activeTab === 'medications' && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Medications</h5>
                        <Button variant="primary" onClick={() => setShowMedicationModal(true)}>
                            <Plus /> Add Medication
                        </Button>
                    </div>
                    
                    {medications.length === 0 ? (
                        <Card className="text-center p-4">
                            <p className="text-muted">No medications tracked yet. Add your medications to keep track of your prescriptions!</p>
                        </Card>
                    ) : (
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Medication</th>
                                    <th>Dosage</th>
                                    <th>Frequency</th>
                                    <th>Start Date</th>
                                    <th>End Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {medications.map(medication => (
                                    <tr key={medication.id}>
                                        <td>{medication.name}</td>
                                        <td>{medication.dosage || '-'}</td>
                                        <td>{medication.frequency || '-'}</td>
                                        <td>{new Date(medication.start_date).toLocaleDateString()}</td>
                                        <td>{medication.end_date ? new Date(medication.end_date).toLocaleDateString() : 'Ongoing'}</td>
                                        <td>
                                            <Badge bg={medication.is_active ? 'success' : 'secondary'}>
                                                {medication.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button variant="outline-primary" size="sm" onClick={() => handleEditMedication(medication)}>
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
            )}

            {activeTab === 'sick-leave' && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Sick Leave</h5>
                        <Button variant="primary" onClick={() => setShowSickLeaveModal(true)}>
                            <Plus /> Add Sick Leave
                        </Button>
                    </div>
                    
                    {sickLeaves.length === 0 ? (
                        <Card className="text-center p-4">
                            <p className="text-muted">No sick leave records yet. Track your sick days here!</p>
                        </Card>
                    ) : (
                        <div className="sick-leave-list">
                            {sickLeaves.map(leave => (
                                <Card key={leave.id} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6>{leave.reason}</h6>
                                                <p className="text-muted">
                                                    {new Date(leave.start_date).toLocaleDateString()} 
                                                    {leave.end_date && ` - ${new Date(leave.end_date).toLocaleDateString()}`}
                                                    {leave.days_taken && ` (${leave.days_taken} days)`}
                                                </p>
                                                {leave.doctor_note && <p><strong>Doctor's Note:</strong> {leave.doctor_note}</p>}
                                                <Badge bg={leave.is_paid ? 'success' : 'warning'}>
                                                    {leave.is_paid ? 'Paid Leave' : 'Unpaid Leave'}
                                                </Badge>
                                            </div>
                                            <Button variant="outline-primary" size="sm">
                                                Edit
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'events' && (
                <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5>Health Events</h5>
                        <Button variant="primary" onClick={() => setShowHealthEventModal(true)}>
                            <Plus /> Add Event
                        </Button>
                    </div>
                    
                    {healthEvents.length === 0 ? (
                        <Card className="text-center p-4">
                            <p className="text-muted">No health events logged yet. Track incidents, symptoms, or health-related events here!</p>
                        </Card>
                    ) : (
                        <div className="health-events-list">
                            {healthEvents.map(event => (
                                <Card key={event.id} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <h6>{event.title}</h6>
                                                <p className="text-muted mb-2">
                                                    {new Date(event.event_date).toLocaleDateString()}
                                                </p>
                                                {event.description && <p><strong>Description:</strong> {event.description}</p>}
                                                {event.symptoms && <p><strong>Symptoms:</strong> {event.symptoms}</p>}
                                                {event.possible_cause && <p><strong>Possible Cause:</strong> {event.possible_cause}</p>}
                                                {event.actions_taken && <p><strong>Actions Taken:</strong> {event.actions_taken}</p>}
                                                {event.outcome && <p><strong>Outcome:</strong> {event.outcome}</p>}
                                                <Badge bg={
                                                    event.severity === 'Critical' ? 'danger' : 
                                                    event.severity === 'High' ? 'warning' : 
                                                    event.severity === 'Medium' ? 'info' : 'secondary'
                                                }>
                                                    {event.severity} Severity
                                                </Badge>
                                            </div>
                                            <Button variant="outline-primary" size="sm" onClick={() => handleEditHealthEvent(event)}>
                                                Edit
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Add/Edit Doctor Modal */}
            <Modal show={showDoctorModal} onHide={() => {setShowDoctorModal(false); setSelectedDoctor(null);}}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedDoctor ? 'Edit Doctor' : 'Add Doctor'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitDoctor}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control name="name" defaultValue={selectedDoctor?.name || ''} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Specialty</Form.Label>
                            <Form.Control name="specialty" defaultValue={selectedDoctor?.specialty || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Clinic/Hospital</Form.Label>
                            <Form.Control name="clinic_hospital" defaultValue={selectedDoctor?.clinic_hospital || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control name="phone" defaultValue={selectedDoctor?.phone || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control name="email" type="email" defaultValue={selectedDoctor?.email || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control name="address" as="textarea" rows={2} defaultValue={selectedDoctor?.address || ''} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control name="notes" as="textarea" rows={2} defaultValue={selectedDoctor?.notes || ''} />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowDoctorModal(false); setSelectedDoctor(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedDoctor ? 'Update Doctor' : 'Add Doctor'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Add Medical Record Modal */}
            <Modal show={showRecordModal} onHide={() => setShowRecordModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Medical Record</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        try {
                            console.log('Submitting form data:', Object.fromEntries(formData));
                            await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/medical-records`, formData);
                            fetchMedicalRecords();
                            setShowRecordModal(false);
                            e.target.reset();
                        } catch (error) {
                            console.error('Error adding medical record:', error);
                            console.error('Error response:', error.response?.data);
                        }
                    }}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Visit Date *</Form.Label>
                                    <Form.Control name="visit_date" type="date" required />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Doctor</Form.Label>
                                    <Form.Select name="doctor_id">
                                        <option value="">Select Doctor</option>
                                        {doctors.map(doctor => (
                                            <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Visit Type</Form.Label>
                                    <Form.Select name="visit_type">
                                        <option value="Regular Checkup">Regular Checkup</option>
                                        <option value="Emergency">Emergency</option>
                                        <option value="Follow-up">Follow-up</option>
                                        <option value="Consultation">Consultation</option>
                                        <option value="Surgery">Surgery</option>
                                        <option value="Test/Lab">Test/Lab</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Cost</Form.Label>
                                    <Form.Control name="cost" type="number" step="0.01" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Symptoms</Form.Label>
                            <Form.Control name="symptoms" as="textarea" rows={2} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Diagnosis</Form.Label>
                            <Form.Control name="diagnosis" as="textarea" rows={2} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Treatment</Form.Label>
                            <Form.Control name="treatment" as="textarea" rows={2} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Prescription</Form.Label>
                            <Form.Control name="prescription" as="textarea" rows={2} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control name="notes" as="textarea" rows={3} />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowRecordModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit">Add Record</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Add/Edit Medication Modal */}
            <Modal show={showMedicationModal} onHide={() => {setShowMedicationModal(false); setSelectedMedication(null);}}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedMedication ? 'Edit Medication' : 'Add Medication'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitMedication}>
                        <Form.Group className="mb-3">
                            <Form.Label>Medication Name *</Form.Label>
                            <Form.Control name="name" defaultValue={selectedMedication?.name || ''} required />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Dosage</Form.Label>
                                    <Form.Control name="dosage" defaultValue={selectedMedication?.dosage || ''} placeholder="e.g., 500mg" />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Frequency</Form.Label>
                                    <Form.Control name="frequency" defaultValue={selectedMedication?.frequency || ''} placeholder="e.g., Twice daily" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date *</Form.Label>
                                    <Form.Control 
                                        name="start_date" 
                                        type="date" 
                                        defaultValue={selectedMedication ? selectedMedication.start_date.split('T')[0] : ''} 
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control 
                                        name="end_date" 
                                        type="date" 
                                        defaultValue={selectedMedication && selectedMedication.end_date ? selectedMedication.end_date.split('T')[0] : ''} 
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control 
                                name="notes" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedMedication?.notes || ''} 
                                placeholder="Instructions, side effects, etc." 
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowMedicationModal(false); setSelectedMedication(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedMedication ? 'Update Medication' : 'Add Medication'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Add Sick Leave Modal */}
            <Modal show={showSickLeaveModal} onHide={() => setShowSickLeaveModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Sick Leave</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        try {
                            await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/sick-leave`, formData);
                            fetchSickLeaves();
                            setShowSickLeaveModal(false);
                            e.target.reset();
                        } catch (error) {
                            console.error('Error adding sick leave:', error);
                        }
                    }}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date *</Form.Label>
                                    <Form.Control name="start_date" type="date" required />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control name="end_date" type="date" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Reason *</Form.Label>
                            <Form.Control name="reason" required placeholder="e.g., Flu, Surgery recovery" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Doctor's Note</Form.Label>
                            <Form.Control name="doctor_note" as="textarea" rows={2} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check 
                                name="is_paid" 
                                type="checkbox" 
                                label="Paid Leave" 
                                defaultChecked 
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => setShowSickLeaveModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit">Add Sick Leave</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Add/Edit Health Event Modal */}
            <Modal show={showHealthEventModal} onHide={() => {setShowHealthEventModal(false); setSelectedHealthEvent(null);}}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedHealthEvent ? 'Edit Health Event' : 'Add Health Event'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmitHealthEvent}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Event Date *</Form.Label>
                                    <Form.Control 
                                        name="event_date" 
                                        type="date" 
                                        defaultValue={selectedHealthEvent ? selectedHealthEvent.event_date.split('T')[0] : ''} 
                                        required 
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Severity</Form.Label>
                                    <Form.Select name="severity" defaultValue={selectedHealthEvent?.severity || 'Low'}>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Title *</Form.Label>
                            <Form.Control 
                                name="title" 
                                defaultValue={selectedHealthEvent?.title || ''} 
                                required 
                                placeholder="e.g., Headache, Fall, Allergic reaction" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control 
                                name="description" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedHealthEvent?.description || ''} 
                                placeholder="What happened?" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Symptoms</Form.Label>
                            <Form.Control 
                                name="symptoms" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedHealthEvent?.symptoms || ''} 
                                placeholder="What symptoms did you experience?" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Possible Cause</Form.Label>
                            <Form.Control 
                                name="possible_cause" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedHealthEvent?.possible_cause || ''} 
                                placeholder="What might have caused this?" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Actions Taken</Form.Label>
                            <Form.Control 
                                name="actions_taken" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedHealthEvent?.actions_taken || ''} 
                                placeholder="What did you do about it?" 
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Outcome</Form.Label>
                            <Form.Control 
                                name="outcome" 
                                as="textarea" 
                                rows={2} 
                                defaultValue={selectedHealthEvent?.outcome || ''} 
                                placeholder="How did it resolve?" 
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={() => {setShowHealthEventModal(false); setSelectedHealthEvent(null);}}>Cancel</Button>
                            <Button variant="primary" type="submit">{selectedHealthEvent ? 'Update Event' : 'Add Event'}</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default MedicalRecords;
